import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
////////////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { SettingOption, PageTitle, BackButton } from '@/app/shared/components';
import { setNetworkSelected } from '@/core/redux/slices';
import { NetworkService } from '@/core/services';
import { networkData } from '@/core/utils';
import { Option } from '@/core/interfaces';
import { useEffect } from 'react';

/**
 * ChooseNetwork component allows users to select a network from a list of available networks.
 *
 * Hooks:
 * - useCustomDispatch: Provides the dispatch function for Redux actions.
 * - useTranslation: Provides translation functions for internationalization support.
 * - useCustomSelector: Retrieves the currently selected network from the Redux store.
 * - useEffect: Sends a pageview event to Google Analytics when the component mounts.
 *
 * Functions:
 * - handleOnSelectedNetworkChange: Handles the network selection change event, updates the network,
 *   and dispatches the updated network to the Redux store. Displays toast notifications for loading,
 *   success, and error states.
 *
 * Template:
 * - Renders a BackButton and PageTitle component.
 * - Maps over the networkData array to render SettingOption components for each network.
 * - The selected network is highlighted, and selecting a different network triggers the handleOnSelectedNetworkChange function.
 */
const ChooseNetwork: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { networkSelected } = useCustomSelector((state) => state.network);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/settings/network',
			title: 'Choose Network',
		});
	}, []);

	/*********************
	// #region Functions  
	**********************/
	const handleOnSelectedNetworkChange = async (selectedNetwork: Option) => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		toast.promise(
			NetworkService.updateSelectedNetwork({
				id: selectedNetwork.id,
			}),
			{
				loading: t('general.loading'),
				success: () => {
					dispatch(setNetworkSelected(selectedNetwork));
					return `${t('settings.networks.updateMessage')} ${
						selectedNetwork.name
					}.`;
				},
				error: t('settings.networks.errorUpdate'),
			}
		);
	};
	2;
	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-2 pt-8 pb-32 mx-4">
			<BackButton />
			<div className="flex flex-col items-center">
				<PageTitle>{t('settings.networks.title')}</PageTitle>
			</div>
			<div className="flex flex-col w-full gap-3">
				{networkData.map((element) => {
					return (
						<SettingOption
							key={element.id}
							option={element}
							isChecked={networkSelected.id == element.id}
							onSelectOption={(selectedNet) => {
								handleOnSelectedNetworkChange(selectedNet);
							}}
						/>
					);
				})}
			</div>
		</div>
	);
};

export default ChooseNetwork;
